<script setup lang="ts">
import type { MessageAction } from '../../types/chat'

import { computed } from 'vue'

interface Props {
  show: boolean
  messageRole: 'user' | 'assistant' | 'system'
  messageTimestamp?: number
  position: { x: number, y: number }
  /** 当前消息是否已显示翻译 */
  isTranslated?: boolean
}

interface Emits {
  (e: 'action', action: MessageAction): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// 根据消息角色决定显示哪些操作
const availableActions = computed(() => {
  const actions: Array<{
    key: MessageAction
    label: string
    icon: string
    color?: string
  }> = []

  // 翻译按钮（所有消息都可以翻译）
  if (props.isTranslated) {
    actions.push({ key: 'translate', label: '收起翻译', icon: 'i-solar:minimize-square-bold-duotone' })
  }
  else {
    actions.push({ key: 'translate', label: '翻译', icon: 'i-solar:translation-2-bold-duotone' })
  }

  if (props.messageRole === 'user') {
    actions.push(
      { key: 'quote', label: '引用', icon: 'i-solar:chat-round-line-bold-duotone' },
      { key: 'edit', label: '编辑', icon: 'i-solar:pen-2-bold-duotone' },
      { key: 'recall', label: '撤回', icon: 'i-solar:undo-left-round-bold-duotone', color: 'text-amber-400' },
      { key: 'delete', label: '删除', icon: 'i-solar:trash-bin-trash-bold-duotone', color: 'text-red-500' },
      { key: 'multiselect', label: '多选', icon: 'i-solar:checklist-bold-duotone' },
    )
  }
  else if (props.messageRole === 'assistant') {
    actions.push(
      { key: 'quote', label: '引用', icon: 'i-solar:chat-round-line-bold-duotone' },
      { key: 'edit', label: '编辑', icon: 'i-solar:pen-2-bold-duotone' },
      { key: 'regenerate', label: '重新生成', icon: 'i-solar:refresh-bold-duotone' },
      { key: 'delete', label: '删除', icon: 'i-solar:trash-bin-trash-bold-duotone', color: 'text-red-500' },
      { key: 'multiselect', label: '多选', icon: 'i-solar:checklist-bold-duotone' },
    )
  }

  return actions
})

// 菜单宽度：根据屏幕宽度动态计算（屏幕的 75%，但不超过 320px 不小于 180px）
const menuDynamicWidth = computed(() => {
  const screenWidth = window.innerWidth
  const padding = 10
  return Math.max(180, Math.min(screenWidth * 0.75, 320, screenWidth - padding * 2))
})

// 菜单样式 - 带边界检测
const menuStyle = computed(() => {
  const screenWidth = window.innerWidth
  const padding = 10

  let x = props.position.x
  const halfWidth = menuDynamicWidth.value / 2

  // 边界检测
  if (x - halfWidth < padding) {
    x = padding + halfWidth
  }
  else if (x + halfWidth > screenWidth - padding) {
    x = screenWidth - padding - halfWidth
  }

  return {
    left: `${x}px`,
    top: `${props.position.y}px`,
    transform: `translate(-50%, -100%)`,
    width: `${menuDynamicWidth.value}px`,
  }
})

function handleAction(action: MessageAction) {
  emit('action', action)
  emit('close')
}

function handleClickOutside() {
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <!-- 背景遮罩 -->
    <Transition name="fade">
      <div
        v-if="show"
        fixed inset-0 z-splash
        @click="handleClickOutside"
        @touchend="handleClickOutside"
      />
    </Transition>

    <!-- 操作菜单 -->
    <Transition name="scale-fade">
      <div
        v-if="show"
        fixed z-activation
        class="message-actions-menu"
        :style="menuStyle"
      >
        <div
          class="actions-grid"
          rounded-xl p-2 shadow-lg
          bg="neutral-700/90 dark:neutral-800/90"
          backdrop-blur-sm
        >
          <button
            v-for="action in availableActions"
            :key="action.key"
            class="action-item"
            :class="[action.color || 'text-white']"
            @click.stop="handleAction(action.key)"
          >
            <div :class="action.icon" class="action-icon" />
            <span class="action-label">{{ action.label }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.message-actions-menu {
  margin-top: -10px;
  max-width: calc(100vw - 20px);
}

/* 网格布局：自动换行，宽度由父容器（JS 动态计算）控制 */
.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(52px, 1fr));
  gap: 2px;
  width: 100%;
}

/* 每个操作按钮：图标在上、文字在下 */
.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 4px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;
  min-width: 0; /* 防止溢出 */
}

.action-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.action-item:active {
  background: rgba(255, 255, 255, 0.2);
}

.action-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.action-label {
  font-size: 10px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.2s ease;
}

.scale-fade-enter-from,
.scale-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -100%) scale(0.9);
}
</style>
